// Which i18n key an auth failure should be SHOWN as, or null to fall back to
// whatever the identity provider said.
//
// ⚠️ Exists because both surfaces render `err.message ?? t("auth.signInFailed")`
// and both build that error through `toAuthError` (below), which already
// defaults `message` to a string: the `??` can never fire, so the athlete
// always read the provider's own English copy, even for a wrong password.
// Measured in a real browser on the served build, not deduced.
//
// ⛔ Two privacy contracts, one table each. The two codes that mean "that pair
// did not authenticate" map to the SAME string on purpose: telling them apart
// would let the page enumerate which addresses have an account. The codes
// below them (unconfirmed, reset required, rate limit) say nothing about
// whether an address exists beyond what the pool already answered, and each
// asks the athlete for a different gesture, so each has its own copy.
const CREDENTIAL_FAILURE_CODES = new Set([
  "NotAuthorizedException",
  "UserNotFoundException",
]);

const SIGN_IN_KEYS: Record<string, string> = {
  UserNotConfirmedException: "auth.accountUnconfirmed",
  PasswordResetRequiredException: "auth.passwordResetRequired",
  TooManyRequestsException: "auth.forgotTooManyAttempts",
  LimitExceededException: "auth.forgotTooManyAttempts",
};

export const signInErrorKey = (code: string | undefined): string | null => {
  if (code === undefined) return null;
  if (CREDENTIAL_FAILURE_CODES.has(code)) return "auth.signInFailed";
  return SIGN_IN_KEYS[code] ?? null;
};

// Change password (signed in, Settings). Here a NotAuthorizedException means
// the CURRENT password is wrong: the athlete is already authenticated, so there
// is nothing to enumerate, and "check your email and password" would be false.
const CHANGE_PASSWORD_KEYS: Record<string, string> = {
  NotAuthorizedException: "mobile.account.currentPasswordWrong",
  InvalidPasswordException: "auth.passwordRejected",
  InvalidParameterException: "auth.passwordRejected",
  TooManyRequestsException: "auth.forgotTooManyAttempts",
  LimitExceededException: "auth.forgotTooManyAttempts",
};

export const changePasswordErrorKey = (
  code: string | undefined,
): string | null =>
  code === undefined ? null : (CHANGE_PASSWORD_KEYS[code] ?? null);

// ⛔ Una SECONDA tabella, nello stesso file, invece di allargare quella sopra.
// I quattro codici con cui Cognito rifiuta la chiusura di un recupero vogliono
// l'opposto di ciò che `signInErrorKey` garantisce: lì due codici collassano in
// una stringa opaca perché distinguerli direbbe quali indirizzi hanno un
// account; qui il codice è già stato spedito a quell'indirizzo, non c'è niente
// da nascondere, e la copia per-codice è l'unico dettaglio azionabile — «l'hai
// sbagliato» e «è scaduto» chiedono due gesti diversi. Un solo posto per «che
// codice Cognito dice cosa», due tabelle perché sono due contratti di privacy.
//
// I codici sono quelli documentati su `ConfirmForgotPassword`. ⚠️ I tentativi
// li conta Cognito, non noi: ⛔ nessun contatore nel client.
const FORGOT_ERROR_KEYS: Record<string, string> = {
  CodeMismatchException: "auth.forgotCodeWrong",
  ExpiredCodeException: "auth.forgotCodeExpired",
  TooManyFailedAttemptsException: "auth.forgotTooManyAttempts",
  // Due codici diversi per la stessa cosa vista dall'atleta — ha chiesto
  // troppo, riprovi più tardi — e nessuno dei due dice nulla sull'account.
  LimitExceededException: "auth.forgotTooManyRequests",
  TooManyRequestsException: "auth.forgotTooManyRequests",
};

export const forgotPasswordErrorKey = (
  code: string | undefined,
): string | null => (code === undefined ? null : (FORGOT_ERROR_KEYS[code] ?? null));

// Il `code` e il testo di un rigetto Cognito, letti da un `unknown`.
//
// ⛔ Vivono qui e non nei mount perché il codice deve SOPRAVVIVERE fino al form
// condiviso: i due montaggi facevano `throw new Error(message)`, che tiene la
// frase inglese e butta via l'unica cosa da cui si può scegliere una
// traduzione. La forma `{ code, message }` è quella che entrambi i
// `toAuthError` producono già.
export const authErrorCode = (err: unknown): string | undefined => {
  if (err === null || typeof err !== "object") return undefined;
  const code = (err as { code?: unknown }).code;
  return typeof code === "string" ? code : undefined;
};

export const authErrorText = (err: unknown, fallback: string): string => {
  if (err === null || typeof err !== "object") return fallback;
  const message = (err as { message?: unknown }).message;
  return typeof message === "string" && message.length > 0 ? message : fallback;
};

/** What both `cognito.ts` mounts reject with. A PLAIN object, not an `Error`:
 *  handed raw to a component that does `String(err)` it prints "[object
 *  Object]". One definition here, because the two mounts are twins. */
export type AuthError = { code: string; message: string };

export const toAuthError = (err: unknown): AuthError => {
  if (err && typeof err === "object") {
    const e = err as { code?: string; name?: string; message?: string };
    return {
      code: e.code ?? e.name ?? "UnknownError",
      message: e.message ?? "Unknown error",
    };
  }
  return { code: "UnknownError", message: String(err) };
};
